import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { ReactNode, useState } from 'react';
import { useTranslate } from 'react-admin';
import { TIME_FORMAT } from 'src/configs/common.config';
import { PermissionButton } from 'src/routes/components/permission-button';
import { MoneyRequest, MoneyRequestStatus } from '../../services/moneyRequestApi';

export const STATUS_COLOR: Record<
  MoneyRequestStatus,
  'default' | 'info' | 'success' | 'warning' | 'error'
> = {
  PENDING: 'info',
  CANCELLED: 'default',
  REJECTED: 'default',
  EXPIRED: 'default',
  EXECUTING: 'warning',
  EXECUTED: 'success',
  FAILED: 'error',
  UNKNOWN: 'warning',
};

export const REQUEST_STATUSES: MoneyRequestStatus[] = [
  'PENDING',
  'EXECUTING',
  'EXECUTED',
  'FAILED',
  'UNKNOWN',
  'REJECTED',
  'CANCELLED',
  'EXPIRED',
];

/** A request whose outcome nobody knows: UNKNOWN, or EXECUTING for too long. */
export const isUnresolved = (r: Pick<MoneyRequest, 'status' | 'stalled'>) =>
  r.status === 'UNKNOWN' || (r.status === 'EXECUTING' && r.stalled);

interface Permission {
  resource: string;
  action: string;
}

export interface RequestsTableProps {
  rows: MoneyRequest[];
  loading?: boolean;
  /** What the request is about, one cell of text. */
  summary: (r: MoneyRequest) => ReactNode;
  /** The signed-in admin's id; the requester can never decide their own request. */
  currentUserId?: string;
  /** Permission that shows Approve / Reject. */
  approvePermission: Permission;
  /** Permission that shows Cancel on one's own PENDING requests. */
  cancelPermission: Permission;
  busyId?: string | null;
  /** Ids whose approval was sent and whose result is not known: Approve stays hidden. */
  attemptedIds?: Set<string>;
  onApprove: (r: MoneyRequest) => void;
  onReject: (r: MoneyRequest, reason: string) => void;
  onCancel: (r: MoneyRequest) => void;
  /** Extra buttons per row (for example Reconcile). */
  extraActions?: (r: MoneyRequest) => ReactNode;
  /** Shown under a row whose outcome is unknown. */
  unresolvedHint?: string;
}

const fmt = (iso?: string | null) => (iso ? dayjs(iso).format(TIME_FORMAT) : '');

export function RequestsTable({
  rows,
  loading,
  summary,
  currentUserId,
  approvePermission,
  cancelPermission,
  busyId,
  attemptedIds,
  onApprove,
  onReject,
  onCancel,
  extraActions,
  unresolvedHint,
}: RequestsTableProps) {
  const t = useTranslate();
  const [rejecting, setRejecting] = useState<MoneyRequest | null>(null);
  const [reason, setReason] = useState('');

  const closeReject = () => {
    setRejecting(null);
    setReason('');
  };

  return (
    <>
      <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('module.moneyRequests.created', { _: 'Created' })}</TableCell>
              <TableCell>{t('module.moneyRequests.requestedBy', { _: 'Requested by' })}</TableCell>
              <TableCell>{t('module.moneyRequests.details', { _: 'Details' })}</TableCell>
              <TableCell>{t('module.moneyRequests.status', { _: 'Status' })}</TableCell>
              <TableCell>{t('module.moneyRequests.decision', { _: 'Decision' })}</TableCell>
              <TableCell align="right">
                {t('module.moneyRequests.actions', { _: 'Actions' })}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.moneyRequests.loading', { _: 'Loading...' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('module.moneyRequests.none', { _: 'No requests' })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => {
                const own = !!currentUserId && r.requestedBy.id === currentUserId;
                const busy = busyId === r.id;
                const unresolved = isUnresolved(r);
                const canDecide = r.status === 'PENDING' && !own && !attemptedIds?.has(r.id);
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmt(r.createdAt)}</TableCell>
                    <TableCell>{r.requestedBy.username}</TableCell>
                    <TableCell sx={{ maxWidth: 360, wordBreak: 'break-all' }}>
                      {summary(r)}
                      {r.requestReason ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.requestReason}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={STATUS_COLOR[r.status] ?? 'default'}
                        label={
                          r.status === 'EXECUTING' && r.stalled
                            ? t('module.moneyRequests.stalled', {
                                _: 'Stalled - outcome unknown',
                              })
                            : r.status
                        }
                      />
                      {r.status === 'PENDING' ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {t('module.moneyRequests.expires', { _: 'Expires' })} {fmt(r.expiresAt)}
                        </Typography>
                      ) : null}
                      {r.txHash ? (
                        <Tooltip title={r.txHash} arrow>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                            sx={{ wordBreak: 'break-all' }}
                          >
                            tx {r.txHash.slice(0, 8)}...{r.txHash.slice(-6)}
                          </Typography>
                        </Tooltip>
                      ) : null}
                      {r.error ? (
                        <Typography
                          variant="caption"
                          color="error"
                          display="block"
                          sx={{ wordBreak: 'break-word' }}
                        >
                          {r.error}
                        </Typography>
                      ) : null}
                      {unresolved && unresolvedHint ? (
                        <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
                          {unresolvedHint}
                        </Alert>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {r.decidedBy ? (
                        <>
                          <Typography variant="body2">{r.decidedBy.username}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {fmt(r.decidedAt)}
                          </Typography>
                        </>
                      ) : null}
                      {r.decisionReason ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {r.decisionReason}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {canDecide ? (
                          <PermissionButton
                            resource={approvePermission.resource}
                            action={approvePermission.action}
                          >
                            <Button
                              size="small"
                              variant="contained"
                              disabled={busy}
                              onClick={() => onApprove(r)}
                            >
                              {t('module.moneyRequests.approve', { _: 'Approve' })}
                            </Button>
                          </PermissionButton>
                        ) : null}
                        {canDecide ? (
                          <PermissionButton
                            resource={approvePermission.resource}
                            action={approvePermission.action}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              disabled={busy}
                              onClick={() => setRejecting(r)}
                            >
                              {t('module.moneyRequests.reject', { _: 'Reject' })}
                            </Button>
                          </PermissionButton>
                        ) : null}
                        {r.status === 'PENDING' && own ? (
                          <PermissionButton
                            resource={cancelPermission.resource}
                            action={cancelPermission.action}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={busy}
                              onClick={() => onCancel(r)}
                            >
                              {t('module.moneyRequests.cancel', { _: 'Cancel' })}
                            </Button>
                          </PermissionButton>
                        ) : null}
                        {extraActions ? extraActions(r) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={!!rejecting} onClose={closeReject} maxWidth="xs" fullWidth>
        <DialogTitle>{t('module.moneyRequests.rejectTitle', { _: 'Reject request' })}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <TextField
              autoFocus
              required
              fullWidth
              size="small"
              label={t('module.moneyRequests.reason', { _: 'Reason' })}
              value={reason}
              inputProps={{ maxLength: 500 }}
              onChange={(e) => setReason(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeReject}>{t('actions.cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            disabled={!reason.trim()}
            onClick={() => {
              if (rejecting) onReject(rejecting, reason.trim());
              closeReject();
            }}
          >
            {t('module.moneyRequests.reject', { _: 'Reject' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

/** One-line description of a request payload, as `key: value` pairs. */
export function describePayload(payload: Record<string, any>, keys: string[]): string {
  return keys
    .filter((k) => payload[k] !== undefined && payload[k] !== null && payload[k] !== '')
    .map((k) => `${k}: ${payload[k]}`)
    .join(' | ');
}
